import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * mezuzi 169.0 #105: leaving the field with the line's quantity unchanged posts nothing. Tinker's setQuantity() (on:blur) always
   * dispatched an update -> a no-op POST /cart/change and a cart re-render that threw keyboard focus to <body>. The committed
   * quantity is the input's defaultValue (its rendered `value`, per line); data-cart-quantity is the variant's whole-cart count.
   * @param {Event} event - The blur event.
   */
  setQuantity(event) {
    const input = event.target;
    if (input instanceof HTMLInputElement && input.value !== '' && Number(input.value) === Number(input.defaultValue)) return;
    super.setQuantity(event);
  }

  /**
   * mezuzi 170.0 #105(3): Enter in the cart DRAWER's quantity field applies the quantity and stays, as the source does (one
   * POST /cart/change, no navigation, focus on the field). The field sits inside the cart form, so Enter submitted it = checkout.
   * IME composition and modified keys pass through; the /cart page keeps the form's own Enter.
   */
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onEnter);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onEnter);
  }

  /** @param {KeyboardEvent} event */
  #onEnter = (event) => {
    if (event.key !== 'Enter' || event.isComposing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input !== this.refs.quantityInput || !this.closest('#cart-drawer')) return;
    event.preventDefault();
    this.setQuantity({ target: input, preventDefault() {} });
  };

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
